import { Injectable } from "@angular/core";
import { AngularFireDatabase } from "@angular/fire/database";

export interface HistogramBin {
  binLabel: string;
  binStart: number;
  binEnd: number;
  count: number;
  percentage: number;
}

export interface SurveyStatistics {
  medianCompletionTimeMs: number;
  medianCompletionTimeFormatted: string;
  averageCompletionTimeMs: number;
  averageCompletionTimeFormatted: string;
  fastestCompletionTimeMs: number;
  fastestCompletionTimeFormatted: string;
  slowestCompletionTimeMs: number;
  slowestCompletionTimeFormatted: string;
  p10CompletionTimeMs: number;
  p10CompletionTimeFormatted: string;
  p90CompletionTimeMs: number;
  p90CompletionTimeFormatted: string;
  p25CompletionTimeMs: number;
  p25CompletionTimeFormatted: string;
  p75CompletionTimeMs: number;
  p75CompletionTimeFormatted: string;
  standardDeviationMs: number;
  standardDeviationFormatted: string;
  totalRespondents: number;
  completedRespondents: number;
  histogram: HistogramBin[];
}

interface RespondentData {
  completedAt?: number;
  durationMs?: number;
  activeMs?: number;
  [key: string]: any;
}

interface ResponsesSnapshot {
  [respondentId: string]: RespondentData;
}

@Injectable({
  providedIn: "root",
})
export class StatisticsService {
  constructor(private firebase: AngularFireDatabase) {}

  async getSurveyStatistics(surveyId: string): Promise<SurveyStatistics> {
    // MOCK DATA FOR DEMO PURPOSES
    // return this.getMockStatistics();

    // REAL IMPLEMENTATION using manage database
    const snapshot = (
      await this.firebase.database
        .ref(`/SurveyResponses/${surveyId}`)
        .once("value")
    ).val() as ResponsesSnapshot;

    let totalRespondents = 0;
    let completedRespondents = 0;
    const activeTimes: number[] = [];

    if (snapshot) {
      for (const respondentKey in snapshot) {
        if (snapshot.hasOwnProperty(respondentKey)) {
          const respondent = snapshot[respondentKey];
          totalRespondents++;

          if (respondent?.completedAt && respondent?.activeMs !== undefined) {
            completedRespondents++;
            activeTimes.push(respondent.activeMs);
          }
        }
      }
    }

    const medianCompletionTimeMs = this.calculateMedian(activeTimes);
    const averageCompletionTimeMs = this.calculateAverage(activeTimes);
    const fastestCompletionTimeMs = this.calculateMin(activeTimes);
    const slowestCompletionTimeMs = this.calculateMax(activeTimes);
    const p10CompletionTimeMs = this.calculatePercentile(activeTimes, 10);
    const p90CompletionTimeMs = this.calculatePercentile(activeTimes, 90);
    const p25CompletionTimeMs = this.calculatePercentile(activeTimes, 25);
    const p75CompletionTimeMs = this.calculatePercentile(activeTimes, 75);
    const standardDeviationMs = this.calculateStandardDeviation(
      activeTimes,
      averageCompletionTimeMs,
    );
    const histogram = this.calculateHistogram(activeTimes);

    return {
      medianCompletionTimeMs,
      medianCompletionTimeFormatted: this.formatTime(medianCompletionTimeMs),
      averageCompletionTimeMs,
      averageCompletionTimeFormatted: this.formatTime(averageCompletionTimeMs),
      fastestCompletionTimeMs,
      fastestCompletionTimeFormatted: this.formatTime(fastestCompletionTimeMs),
      slowestCompletionTimeMs,
      slowestCompletionTimeFormatted: this.formatTime(slowestCompletionTimeMs),
      p10CompletionTimeMs,
      p10CompletionTimeFormatted: this.formatTime(p10CompletionTimeMs),
      p90CompletionTimeMs,
      p90CompletionTimeFormatted: this.formatTime(p90CompletionTimeMs),
      p25CompletionTimeMs,
      p25CompletionTimeFormatted: this.formatTime(p25CompletionTimeMs),
      p75CompletionTimeMs,
      p75CompletionTimeFormatted: this.formatTime(p75CompletionTimeMs),
      standardDeviationMs,
      standardDeviationFormatted: this.formatTime(standardDeviationMs),
      totalRespondents,
      completedRespondents,
      histogram,
    };
  }

  private getMockStatistics(): SurveyStatistics {
    // Generate realistic mock completion times with normal distribution (bell curve)
    // Mean: ~6 minutes (360000ms), Standard deviation: ~1.5 minutes
    // Total: 100 data points for better visualization
    const mockTimes = [
      // 2 very fast (3-3.5 min)
      180000, 195000,
      // 5 fast (3.5-4 min)
      210000, 220000, 230000, 240000, 245000,
      // 10 moderately fast (4-4.5 min)
      250000, 255000, 260000, 265000, 270000, 275000, 280000, 285000, 290000,
      295000,
      // 15 below median (4.5-5.5 min)
      300000, 305000, 310000, 315000, 320000, 325000, 330000, 335000, 340000,
      345000, 350000, 355000, 360000, 365000, 370000,
      // 30 around median (5.5-6.5 min) - PEAK OF BELL CURVE
      375000, 380000, 385000, 390000, 395000, 400000, 405000, 410000, 415000,
      420000, 375000, 380000, 385000, 390000, 395000, 400000, 405000, 410000,
      415000, 420000, 375000, 380000, 385000, 390000, 395000, 400000, 405000,
      410000, 415000, 420000,
      // 20 above median (6.5-7.5 min)
      425000, 430000, 435000, 440000, 445000, 450000, 455000, 460000, 465000,
      470000, 425000, 430000, 435000, 440000, 445000, 450000, 455000, 460000,
      465000, 470000,
      // 12 moderately slow (7.5-8.5 min)
      475000, 480000, 485000, 490000, 495000, 500000, 505000, 510000, 515000,
      520000, 525000, 530000,
      // 5 slow (8.5-9.5 min)
      535000, 545000, 555000, 565000, 575000,
      // 1 very slow (10 min)
      600000,
    ];

    const totalRespondents = 50;
    const completedRespondents = mockTimes.length;

    const medianCompletionTimeMs = this.calculateMedian(mockTimes);
    const averageCompletionTimeMs = this.calculateAverage(mockTimes);
    const fastestCompletionTimeMs = this.calculateMin(mockTimes);
    const slowestCompletionTimeMs = this.calculateMax(mockTimes);
    const p10CompletionTimeMs = this.calculatePercentile(mockTimes, 10);
    const p90CompletionTimeMs = this.calculatePercentile(mockTimes, 90);
    const p25CompletionTimeMs = this.calculatePercentile(mockTimes, 25);
    const p75CompletionTimeMs = this.calculatePercentile(mockTimes, 75);
    const standardDeviationMs = this.calculateStandardDeviation(
      mockTimes,
      averageCompletionTimeMs,
    );
    const histogram = this.calculateHistogram(mockTimes);

    console.log(
      "Mock data - IQR:",
      (p75CompletionTimeMs - p25CompletionTimeMs) / 60000,
      "minutes",
    );
    console.log(
      "Mock data - FD bin width:",
      (2 * (p75CompletionTimeMs - p25CompletionTimeMs)) /
        Math.pow(mockTimes.length, 1 / 3) /
        60000,
      "minutes",
    );
    console.log("Mock data - Histogram bins:", histogram.length);

    return {
      medianCompletionTimeMs,
      medianCompletionTimeFormatted: this.formatTime(medianCompletionTimeMs),
      averageCompletionTimeMs,
      averageCompletionTimeFormatted: this.formatTime(averageCompletionTimeMs),
      fastestCompletionTimeMs,
      fastestCompletionTimeFormatted: this.formatTime(fastestCompletionTimeMs),
      slowestCompletionTimeMs,
      slowestCompletionTimeFormatted: this.formatTime(slowestCompletionTimeMs),
      p10CompletionTimeMs,
      p10CompletionTimeFormatted: this.formatTime(p10CompletionTimeMs),
      p90CompletionTimeMs,
      p90CompletionTimeFormatted: this.formatTime(p90CompletionTimeMs),
      p25CompletionTimeMs,
      p25CompletionTimeFormatted: this.formatTime(p25CompletionTimeMs),
      p75CompletionTimeMs,
      p75CompletionTimeFormatted: this.formatTime(p75CompletionTimeMs),
      standardDeviationMs,
      standardDeviationFormatted: this.formatTime(standardDeviationMs),
      totalRespondents: 125,
      completedRespondents: 100,
      histogram,
    };
  }

  private formatTime(milliseconds: number): string {
    if (milliseconds === 0) {
      return "0:00";
    }
    if (!milliseconds || isNaN(milliseconds)) {
      return "-";
    }
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  private calculateMedian(values: number[]): number {
    if (values.length === 0) return 0;

    values.sort((a, b) => a - b);
    const mid = Math.floor(values.length / 2);

    return values.length % 2 === 0
      ? (values[mid - 1] + values[mid]) / 2
      : values[mid];
  }

  private calculateAverage(values: number[]): number {
    if (values.length === 0) return 0;

    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  }

  private calculateMin(values: number[]): number {
    if (values.length === 0) return 0;
    return Math.min(...values);
  }

  private calculateMax(values: number[]): number {
    if (values.length === 0) return 0;
    return Math.max(...values);
  }

  private calculatePercentile(values: number[], percentile: number): number {
    if (values.length === 0) return 0;

    const sorted = [...values].sort((a, b) => a - b);
    const index = (percentile / 100) * (sorted.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index - lower;

    if (lower === upper) {
      return sorted[lower];
    }

    return sorted[lower] * (1 - weight) + sorted[upper] * weight;
  }

  private calculateStandardDeviation(values: number[], mean: number): number {
    if (values.length === 0) return 0;

    const squaredDifferences = values.map((value) => Math.pow(value - mean, 2));
    const variance =
      squaredDifferences.reduce((acc, val) => acc + val, 0) / values.length;
    return Math.sqrt(variance);
  }

  private calculateHistogram(values: number[]): HistogramBin[] {
    if (values.length === 0) return [];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const n = values.length;

    // Calculate IQR (Interquartile Range)
    const q1 = this.calculatePercentile(values, 25);
    const q3 = this.calculatePercentile(values, 75);
    const iqr = q3 - q1;

    // Freedman-Diaconis rule: binWidth = 2 * IQR / n^(1/3)
    const fdBinWidth = (2 * iqr) / Math.pow(n, 1 / 3);

    // Round min down to nearest minute
    const minRounded = Math.floor(min / 60000) * 60000;
    // Round max up to nearest minute
    const maxRounded = Math.ceil(max / 60000) * 60000;
    const rangeMs = maxRounded - minRounded;

    // Convert FD bin width to minutes and round to sensible interval
    const fdBinMinutes = fdBinWidth / 60000;

    // Round to nearest sensible interval (0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 5, 7.5, 10, 15, 20, 30, 45, 60 minutes)
    let binSizeMinutes: number;
    if (fdBinMinutes <= 0.25) {
      binSizeMinutes = 0.25;
    } else if (fdBinMinutes <= 0.5) {
      binSizeMinutes = 0.5;
    } else if (fdBinMinutes <= 0.75) {
      binSizeMinutes = 0.75;
    } else if (fdBinMinutes <= 1) {
      binSizeMinutes = 1;
    } else if (fdBinMinutes <= 1.5) {
      binSizeMinutes = 1.5;
    } else if (fdBinMinutes <= 2) {
      binSizeMinutes = 2;
    } else if (fdBinMinutes <= 3) {
      binSizeMinutes = 3;
    } else if (fdBinMinutes <= 4) {
      binSizeMinutes = 4;
    } else if (fdBinMinutes <= 5) {
      binSizeMinutes = 5;
    } else if (fdBinMinutes <= 7.5) {
      binSizeMinutes = 7.5;
    } else if (fdBinMinutes <= 10) {
      binSizeMinutes = 10;
    } else if (fdBinMinutes <= 15) {
      binSizeMinutes = 15;
    } else if (fdBinMinutes <= 20) {
      binSizeMinutes = 20;
    } else if (fdBinMinutes <= 30) {
      binSizeMinutes = 30;
    } else if (fdBinMinutes <= 45) {
      binSizeMinutes = 45;
    } else {
      binSizeMinutes = 60;
    }

    const binSizeMs = binSizeMinutes * 60000;
    const binCount = Math.ceil(rangeMs / binSizeMs);

    const bins: HistogramBin[] = [];

    for (let i = 0; i < binCount; i++) {
      const binStart = minRounded + i * binSizeMs;
      const binEnd = minRounded + (i + 1) * binSizeMs;
      const count = values.filter(
        (v) => v >= binStart && (i === binCount - 1 ? v <= binEnd : v < binEnd),
      ).length;
      const percentage = (count / values.length) * 100;

      bins.push({
        binLabel: `${this.formatTime(binStart)} - ${this.formatTime(binEnd)}`,
        binStart,
        binEnd,
        count,
        percentage,
      });
    }

    return bins;
  }
}
